import React, {createContext, useContext, useMemo} from "react";
import {ThemeContext, themes} from "./ThemeContext";
import {BackgroundColorContext, backgroundColors} from "./BackgroundColorContext";
import colors from "../styles/colors";
import {ChartThemeConfig, ChartThemeColors, defaultChartColors, ChartColorCategory} from "../types/chartTheme";

interface ChartThemeContextValue {
  config: ChartThemeConfig;
  getColors: (category?: ChartColorCategory) => string[];
  isDarkMode: boolean;
  getThemeColors: () => ChartThemeColors;
}

const ChartThemeContext = createContext<ChartThemeContextValue | undefined>(undefined);

interface ChartThemeProviderProps {
  children: React.ReactNode;
}

export const ChartThemeProvider: React.FC<ChartThemeProviderProps> = ({children}) => {
  const {theme} = useContext(ThemeContext);
  const {color} = useContext(BackgroundColorContext);

  const isDarkMode = theme === themes.dark;

  const themeColors: ChartThemeColors = useMemo(() => {
    if (isDarkMode) {
      return {
        primary: colors.brand,
        secondary: colors.tacxblue,
        background: "#1e1e1e",
        text: "#ffffff",
        grid: "#888888",
        accent: colors.lightblue,
        surface: "#2d2d2d",
        border: "#555555",
        tooltip: {
          background: "#333333",
          text: "#ffffff",
          border: "#666666"
        }
      };
    } else {
      return {
        primary: colors.brand,
        secondary: colors.tacxblue,
        background: "#ffffff",
        text: "#333333",
        grid: "#e0e0e0",
        accent: colors.wahooblue,
        surface: "#f8f9fa",
        border: "#dee2e6",
        tooltip: {
          background: "#ffffff",
          text: "#333333",
          border: "#cccccc"
        }
      };
    }
  }, [isDarkMode]);

  const config: ChartThemeConfig = useMemo(
    () => ({
      mode: theme as keyof typeof themes,
      colors: themeColors,
      backgroundColorMode: color as keyof typeof backgroundColors,
      customColors: colors
    }),
    [theme, themeColors, color]
  );

  const getColors = (category: ChartColorCategory = "performance"): string[] => {
    const themeMode = isDarkMode ? "dark" : "light";
    return defaultChartColors[category]?.[themeMode]?.default || defaultChartColors.performance[themeMode].default;
  };

  const getThemeColors = (): ChartThemeColors => themeColors;

  const value: ChartThemeContextValue = {
    config,
    getColors,
    isDarkMode,
    getThemeColors
  };

  return <ChartThemeContext.Provider value={value}>{children}</ChartThemeContext.Provider>;
};

export const useChartTheme = (): ChartThemeContextValue => {
  const context = useContext(ChartThemeContext);
  if (!context) {
    throw new Error("useChartTheme must be used within a ChartThemeProvider");
  }
  return context;
};
